/**
 * Time helpers for the CLI sign-in approval page.
 *
 * Split out from the page so the countdown can be tested without a browser:
 * every function takes `now` rather than reading the clock, and none of them
 * touch React.
 *
 * An attempt lives for ten minutes. The page ticks a local timer against
 * `expiresAt` instead of asking the server again, because the approval routes
 * are rate limited and polling one to learn something the payload already said
 * would spend a member's remaining tries.
 */
import { formatDistanceStrict, format } from "date-fns";

const parse = (iso: string | null | undefined): Date | null => {
  if (!iso) return null;
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? null : date;
};

/** Milliseconds left before an attempt expires. Never negative. */
export const msUntil = (iso: string, now: number): number => {
  const date = parse(iso);
  if (!date) return 0;
  return Math.max(0, date.getTime() - now);
};

/**
 * Whether an attempt is past its expiry.
 *
 * An unparseable timestamp counts as expired: the page must never offer an
 * approve button it cannot vouch for.
 */
export const hasExpired = (iso: string, now: number): boolean =>
  msUntil(iso, now) <= 0;

/** How long is left, in the roundest words that are still honest. */
export const countdownLabel = (msLeft: number): string => {
  if (msLeft <= 0) return "expired";
  const seconds = Math.ceil(msLeft / 1000);
  if (seconds < 60) return "in less than a minute";
  const minutes = Math.floor(seconds / 60);
  return minutes === 1 ? "in about 1 minute" : `in about ${minutes} minutes`;
};

/** When the code was asked for, in words: "2 minutes ago". */
export const requestedAgoLabel = (iso: string, now: number): string => {
  const date = parse(iso);
  if (!date) return "at an unknown time";
  return formatDistanceStrict(date, new Date(now), { addSuffix: true });
};

/** The same moment as a date and time the member can compare against. */
export const requestedAtLabel = (iso: string): string => {
  const date = parse(iso);
  if (!date) return "Not recorded";
  return format(date, "d MMM yyyy 'at' HH:mm");
};
